import { z } from "zod";
import { zEmail, zId } from "./common.js";

export const zEventStatus = z.enum(["draft", "published", "completed", "cancelled"]);
export type EventStatus = z.infer<typeof zEventStatus>;

export const zCreateEvent = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(5000).optional(),
  venue: z.string().trim().max(300).optional(),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }).nullable().optional(),
  capacity: z.number().int().min(1).max(100_000).nullable().optional(),
  fundId: zId.optional(),
  campaignId: zId.nullable().optional(),
});
export type CreateEvent = z.infer<typeof zCreateEvent>;

export const zUpdateEvent = zCreateEvent.partial().extend({
  status: zEventStatus.optional(),
});
export type UpdateEvent = z.infer<typeof zUpdateEvent>;

export const zUpsertTicketType = z.object({
  name: z.string().trim().min(1).max(120),
  price: z.number().int().min(0).max(100_000_000),
  /** fair market value of benefits received — the non-deductible portion */
  fairMarketValue: z.number().int().min(0).max(100_000_000).default(0),
  quantity: z.number().int().min(1).max(100_000).nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
});
export type UpsertTicketType = z.infer<typeof zUpsertTicketType>;

/** seating: a named table (or section) with a seat capacity */
export const zUpsertEventTable = z.object({
  name: z.string().trim().min(1).max(120),
  capacity: z.number().int().min(1).max(1000),
  sortOrder: z.number().int().min(0).default(0),
});
export type UpsertEventTable = z.infer<typeof zUpsertEventTable>;

/** move a registration onto a table (optionally a numbered seat) or off (tableId null) */
export const zAssignSeat = z.object({
  tableId: zId.nullable(),
  seatNumber: z.number().int().min(1).max(1000).nullable().optional(),
});
export type AssignSeat = z.infer<typeof zAssignSeat>;

/** staff-side registration */
export const zCreateRegistration = z.object({
  constituentId: zId,
  ticketTypeId: zId.nullable().optional(),
});
export type CreateRegistration = z.infer<typeof zCreateRegistration>;

/** public self-registration */
export const zPublicRegistration = z.object({
  ticketTypeId: zId.nullable().optional(),
  attendee: z.object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    email: zEmail,
  }),
  /** required when the chosen ticket has a price; dev adapter accepts any token except tok_fail */
  paymentToken: z.string().max(500).optional(),
});
export type PublicRegistration = z.infer<typeof zPublicRegistration>;
